import { useState } from "react"
import { KeyedMutator } from "swr"

import { MUST_LOG_OUT_AND_IN } from "./useAddGroup"
import {
    SUPPORT_REFER,
    SUPPORT_REFER_HELP_DESK,
} from "../../../../errors/errorMessages"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { snackbar } from "../../../../utils/general/generalUtils"
import { patchClientProtected } from "../../clients/clients"
import { TUsersOutput } from "../../mappers/adminPortal/activeUsersMapper"
import { UPDATE_QUICKSIGHT_ACCESS } from "../../urls/private"

const NONE = "NONE"

type IUpdateQuicksight = {
    updateQuicksight: (
        userRole: string,
        resetSwitchOnFail?: () => void
    ) => Promise<void>
    isLoading: boolean
}

/**
 * useUpdateQuicksight is a custom hook that handles updating a user's QuickSight access role.
 * It offers a function to update the user's role and manages the loading state during the update process.
 */
const useUpdateQuicksight = (
    email: string,
    fullName: string,
    mutate: KeyedMutator<TUsersOutput>
): IUpdateQuicksight => {
    const [isLoading, setIsLoading] = useState(false)
    const updateQuicksight = async (
        userRole: string,
        resetSwitchOnFail?: () => void
    ): Promise<void> => {
        try {
            setIsLoading(true)
            const encodedEmail = encodeURIComponent(email)
            await patchClientProtected(
                UPDATE_QUICKSIGHT_ACCESS(encodedEmail.toLowerCase(), userRole),
                null
            )
            let msg = `${fullName} successfully granted the role ${userRole}. ${MUST_LOG_OUT_AND_IN}`
            if (userRole === NONE) {
                msg = `Successfully removed the Insight access for ${fullName}. ${MUST_LOG_OUT_AND_IN}`
            }
            snackbar(msg, "success")
            mutate()
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            if (resetSwitchOnFail) {
                resetSwitchOnFail()
            }

            let errorMsg
            if (errorObj?.code === 403) {
                errorMsg = `${errorObj?.message}. Please ${SUPPORT_REFER_HELP_DESK}`
            }
            if (errorObj?.code === 400) {
                errorMsg =
                    `You have exceeded the maximum number of user for ${userRole} role. ` +
                    `Please remove ${userRole} permission from another user to grant ${fullName} this role.`
            } else {
                errorMsg = `Unable to grant ${fullName} this role. Please try again. ${SUPPORT_REFER}`
            }

            snackbar(errorMsg, "warning")
        } finally {
            setIsLoading(false)
        }
    }

    return {
        updateQuicksight,
        isLoading,
    }
}

export default useUpdateQuicksight
